import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { MAINNET_BRIDGE } from "config/default";
import { BigNumber } from "ethers";
import { ClaimButton } from "modules/bridge/ClaimButton";
import { DESTINATION_NETWORK_BY_VALUE } from "modules/bridge/config";
import Link from "next/link";
import { Address } from "viem";

const cellStyle = {
  border: "none",
  paddingY: 3,
  paddingLeft: 0,
  paddingRight: 1,
};

interface Props {
  txHash: string;
  type: string;
  blockNum: string;
  amount: string;
  tokenSymbol: string;
  sourceChainExplorer: string;
  sourceChainName: string;
  destinationChainExplorer: string;
  destinationChainName: string;
  depositReadyForClaim: boolean;
  depositClaimTxHash: string;
  depositAmount: string;
  depositCnt: string;
  destAddr: string;
  origNet: number;
  destNet: number;
  origAddr: string;
  globalIndex: string;
  claimTxHash: string;
}

export const HistoryItem = ({
  txHash,
  type,
  blockNum,
  tokenSymbol,
  amount,
  sourceChainExplorer,
  sourceChainName,
  destinationChainExplorer,
  destinationChainName,
  depositReadyForClaim,
  depositClaimTxHash,
  depositAmount,
  depositCnt,
  destAddr,
  origNet,
  destNet,
  origAddr,
  globalIndex,
  claimTxHash,
}: Props) => {
  return (
    <TableRow>
      <TableCell sx={cellStyle}>
        <Typography variant="body1">{type}</Typography>
        <Typography variant="caption" color="textSecondary">
          Block #{blockNum}
        </Typography>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Typography variant="body2">
          Token: {amount} {tokenSymbol}
        </Typography>
        <Link href={`${sourceChainExplorer}/tx/${txHash}`} target="_blank">
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              alignItems: "center",
              display: "flex",
            }}
          >
            Chain: {sourceChainName}
            <IconButton size="small" color="inherit">
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Typography>
        </Link>
      </TableCell>

      <TableCell sx={cellStyle}>
        <Typography variant="body2">
          Token: {amount} {tokenSymbol}
        </Typography>
        <Link
          href={`${destinationChainExplorer}/tx/${depositClaimTxHash}`}
          target="_blank"
        >
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              alignItems: "center",
              display: "flex",
            }}
          >
            Chain: {destinationChainName}
            <IconButton size="small" color="inherit">
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Typography>
        </Link>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Stack
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {depositReadyForClaim && !depositClaimTxHash ? (
            <ClaimButton
              amount={BigNumber.from(depositAmount || "0")}
              depositCount={Number(depositCnt) || 0}
              depositTxHash={txHash}
              destinationAddress={destAddr as Address}
              from={{
                networkId: origNet,
                bridgeContractAddress: MAINNET_BRIDGE,
              }}
              to={{
                key: "",
                networkId: destNet,
                bridgeContractAddress: MAINNET_BRIDGE,
              }}
              token={{
                address: origAddr as Address,
                chainId:
                  DESTINATION_NETWORK_BY_VALUE[
                    origNet as keyof typeof DESTINATION_NETWORK_BY_VALUE
                  ].chain.id,
              }}
              tokenOriginNetwork={origNet}
              globalIndex={Number(globalIndex)}
            />
          ) : !claimTxHash ? (
            <Button
              href={
                destNet === 1
                  ? `https://eth.blockscout.com/tx/${txHash}`
                  : `https://prom-blockscout.eu-north-2.gateway.fm/tx/${txHash}`
              }
              target="_blank"
              sx={{
                width: "92px",
                height: "32px",
              }}
              variant="outlined"
              startIcon={<CircularProgress size={14} />}
            >
              View
            </Button>
          ) : (
            <CheckCircleIcon width={20} height={20} color="success" />
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};
